import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiEdit3, FiTrash2, FiEye } from "react-icons/fi";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";

const API = process.env.REACT_APP_API_BASE_URL;

const MessageTemplateTable = () => {
  const [rows, setRows] = useState([]);

  const [modalType, setModalType] = useState(null); // create | edit | view
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({
    title: "",
    type: "cancel",
    content: "",
  });

  // 🔥 FETCH
  const fetchData = async () => {
    const res = await axios.get(`${API}/api/message-templates`);
    setRows(res.data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 🔥 STATUS
const toggleStatus = async (id, currentStatus) => {
  const newStatus = currentStatus ? 0 : 1;

  await axios.put(`${API}/api/message-templates/${id}/status`, {
    is_active: newStatus,
  });

  fetchData();
};
  // 🔥 DEFAULT
  const setDefault = async (id) => {
    await axios.put(`${API}/api/message-templates/${id}/set-default`);
    toast.success("Varsayılan güncellendi");
    fetchData();
  };

  // 🔥 DELETE
  const handleDelete = (id) => {
    confirmAlert({
      title: "Sil",
      message: "Silmek istediğine emin misin?",
      buttons: [
        {
          label: "Evet",
          onClick: async () => {
            await axios.delete(`${API}/api/message-templates/${id}`);
            toast.success("Silindi");
            fetchData();
          },
        },
        { label: "Vazgeç" },
      ],
    });
  };

  // 🔥 SAVE
  const handleSave = async () => {
    try {
      if (modalType === "create") {
        await axios.post(`${API}/api/message-templates`, form);
        toast.success("Eklendi");
      } else {
        await axios.put(`${API}/api/message-templates/${selected.id}`, form);
        toast.success("Güncellendi");
      }

      setModalType(null);
      fetchData();
    } catch {
      toast.error("Hata oluştu");
    }
  };

  // 🔥 SWITCH
  const Switch = ({ checked, onChange }) => (
    <div className="form-check form-switch text-center">
      <input
        className="form-check-input"
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ width: 45, height: 22 }}
      />
    </div>
  );

  // 🔥 TABLE
  const columns = useMemo(
    () => [
      { accessorKey: "id", header: "ID", size: 60 },

      { accessorKey: "title", header: "Başlık" },

      {
        accessorKey: "type",
        header: "Tür",
        cell: ({ row }) => {
          const map = {
            cancel: "İptal",
            partial_cancel: "Kısmi İptal",
            refund: "İade",
            custom: "Diğer",
          };
          return map[row.original.type];
        },
      },

      {
        accessorKey: "is_default",
        header: "Varsayılan",
        cell: ({ row }) => (
          <Switch
            checked={!!row.original.is_default}
            onChange={() => setDefault(row.original.id)}
          />
        ),
      },

      {
        accessorKey: "is_active",
        header: "Durum",
        cell: ({ row }) => (
          <Switch
            checked={!!row.original.is_active}
            onChange={() =>
              toggleStatus(row.original.id, row.original.is_active)
            }
          />
        ),
      },

      {
        accessorKey: "actions",
        header: "İşlemler",
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            {/* VIEW */}
            <button
              className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
              onClick={() => {
                setSelected(row.original);
                setForm(row.original);
                setModalType("view");
              }}
            >
              <FiEye />
            </button>

            {/* EDIT */}
            <button
              className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
              onClick={() => {
                setSelected(row.original);
                setForm(row.original);
                setModalType("edit");
              }}
            >
              <FiEdit3 />
            </button>

            <button
              className={`avatar-text avatar-md border-soft-danger ${
                row.original.usage_count > 0
                  ? "text-muted bg-light"
                  : "text-danger bg-soft-danger"
              }`}
              disabled={row.original.usage_count > 0}
              title={
                row.original.usage_count > 0
                  ? "Bu şablon kullanıldığı için silinemez"
                  : "Sil"
              }
              onClick={() => {
                if (row.original.usage_count > 0) return;
                handleDelete(row.original.id);
              }}
            >
              <FiTrash2 />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <>
      {/* 🔥 CREATE BUTTON */}
      <div className="mb-3 d-flex justify-content-end">
        <button
          className="btn btn-primary"
          onClick={() => {
            setForm({ title: "", type: "cancel", content: "" });
            setModalType("create");
          }}
        >
          + Yeni Ekle
        </button>
      </div>

      <Table data={rows} columns={columns} />

      {/* 🔥 MODAL */}
      {modalType && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1040 }}
          ></div>
          <div className="modal fade show d-block" style={{ zIndex: 1050 }}>
            <div
              className="modal-dialog modal-lg"
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                margin: 0,
                width: "100%",
                maxWidth: "800px",
              }}
            >
              <div className="modal-content">
                {/* HEADER */}
                <div className="modal-header">
                  <h5 className="modal-title">
                    {modalType === "create" && "Yeni Ekle"}
                    {modalType === "edit" && "Şablon Düzenle"}
                    {modalType === "view" && "Şablon Detay"}
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => setModalType(null)}
                  />
                </div>

                {/* BODY */}
                <div className="modal-body">
                  {/* TITLE */}
                  <div className="mb-3">
                    <label className="form-label">Başlık</label>
                    <input
                      className="form-control"
                      disabled={modalType === "view"}
                      value={form.title}
                      onChange={(e) =>
                        setForm({ ...form, title: e.target.value })
                      }
                    />
                  </div>

                  {/* TYPE */}
                  <div className="mb-3">
                    <label className="form-label">Tür</label>
                    <select
                      className="form-select"
                      disabled={modalType === "view"}
                      value={form.type}
                      onChange={(e) =>
                        setForm({ ...form, type: e.target.value })
                      }
                    >
                      <option value="cancel">İptal</option>
                      <option value="partial_cancel">Kısmi İptal</option>
                      <option value="refund">İade</option>
            
                      <option value="custom">Diğer</option>
                    </select>
                  </div>

                  {/* CONTENT */}
                  <div className="mb-3">
                    <label className="form-label">Mesaj</label>
                    <textarea
                      className="form-control"
                      rows={6}
                      disabled={modalType === "view"}
                      value={form.content}
                      onChange={(e) =>
                        setForm({ ...form, content: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* FOOTER */}
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setModalType(null)}
                  >
                    Kapat
                  </button>

                  {modalType !== "view" && (
                    <button className="btn btn-primary" onClick={handleSave}>
                      Kaydet
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MessageTemplateTable;
